import React, { useEffect, useMemo, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import InputComponent from "../../../components/Tenant/InputComponent/InputComponent";
import { useFetchAllBatchMutation } from "../../../features/tenant/batch/batchApi";
import { useAddQrCodeMutation } from "../../../features/tenant/qrCode/qrCodeApi";
import { toast } from "react-hot-toast";
import SelectComponentWithSearch from "../../../components/Tenant/SelectComponentWIthSearch/SelectComponentWithSearch";
import { useFetchProductsMutation } from "../../../features/tenant/products/productsApi";
import { useFetchSubcategoriesMutation } from "../../../features/tenant/productHeirarchy/productHeirarchyApi";
import { useNavigate } from "react-router-dom";
import { getVendorName } from "../../../utils/getVendorName";
import { CircleLoaderComponent } from "../../../components/Loader/CircleLoaderComponent";

// Empty "nothing selected" value used by dropdowns
const NONE = { id: -1, name: "" };

// Wraps a field and makes it unusable until previous cascading step is chosen
const LockedField = ({ locked, children }) => (
  <div
    className={locked ? "opacity-50 pointer-events-none select-none" : ""}
    aria-disabled={locked}
    onKeyDownCapture={(e) => {
      if (locked && e.key !== "Tab") {
        e.preventDefault();
        e.stopPropagation();
      }
    }}
  >
    {children}
  </div>
);

const QrGeneration = () => {
  const [batchList, setbatchList] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(NONE);
  const [selectedProductCategory, setSelectedProductCategory] = useState(NONE);
  const [gwProduct, setGwProduct] = useState(NONE);
  const [selectedSize, setSelectedSize] = useState(NONE);
  const [selectedColor, setSelectedColor] = useState(NONE);
  const [qty, setQty] = useState(null);

  const [piecePerBox, setPiecePerBox] = useState(1);
  const [mrpPerPiece, setMrpPerPiece] = useState(0);

  const boxPrice = useMemo(() => {
    if (piecePerBox === "" || piecePerBox === null || piecePerBox === undefined) return 0;
    const m = Number(mrpPerPiece) || 0;
    const p = Number(piecePerBox) || 0;
    const total = m * p;
    return Number.isInteger(total) ? total : parseFloat(total.toFixed(2));
  }, [mrpPerPiece, piecePerBox]);

  const navigate = useNavigate();
  const token = sessionStorage.getItem("access_token");
  const tenantId = JSON.parse(sessionStorage.getItem("vendorData") || "{}").tenant_id;
  const qrType = JSON.parse(sessionStorage.getItem("companyProfile") || "{}").qr_type;

  const [fetchBatchList, { data: batchListData, error: batchListError }] =
    useFetchAllBatchMutation();

  const [
    fetchProducts,
    {
      data: productsData,
      isLoading: productsIsLoading,
      isError: productsIsError,
    },
  ] = useFetchProductsMutation();

  const [
    fetchSubCategories,
    {
      data: subCategoriesData,
      isLoading: subCategoriesIsLoading,
    },
  ] = useFetchSubcategoriesMutation();

  const [
    addQrCode,
    {
      data: QrCodeData,
      isLoading: QrCodeIsLoading,
      error: QrCodeError,
    },
  ] = useAddQrCodeMutation();

  // Initial load
  useEffect(() => {
    fetchSubCategories({
      token,
      tenant_id: tenantId,
    });
    fetchBatchList({
      token,
      tenant_id: tenantId,
      limit: 100,
      offset: 0,
    });
  }, []);

  // When Batches arrive: sort newest first and pre-fill latest batch
  useEffect(() => {
    toast.dismiss();

    if (batchListData?.body?.batches) {
      const batches = batchListData.body.batches;
      if (Array.isArray(batches) && batches.length > 0) {
        const sortedBatches = [...batches].sort((a, b) => {
          const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
          if (timeA !== timeB) return timeB - timeA;
          return (b.id || b.batch_id || 0) - (a.id || a.batch_id || 0);
        });

        setbatchList(sortedBatches);
        setSelectedBatch((prev) => (prev.id === -1 ? sortedBatches[0] : prev));
      } else {
        setbatchList([]);
      }
    }
    if (batchListError) {
      console.log("batch list error", batchListError);
    }
  }, [batchListData, batchListError]);

  // When Category changes: fetch products of this category and reset downstream fields
  useEffect(() => {
    setGwProduct(NONE);
    setSelectedSize(NONE);
    setSelectedColor(NONE);

    if (selectedProductCategory.id === -1) return;

    fetchProducts({
      token,
      tenant_id: tenantId,
      body: { category_name: selectedProductCategory.name },
    });
  }, [selectedProductCategory]);

  useEffect(() => {
    setSelectedSize(NONE);
    setSelectedColor(NONE);
  }, [gwProduct]);

  useEffect(() => {
    setSelectedColor(NONE);
  }, [selectedSize]);

  useEffect(() => {
    if (productsIsError) {
      toast.error("Failed to fetch products. Please try again.");
    }
  }, [productsIsError]);

  // Cascading Option Lists
  const gwProductOptions = useMemo(() => {
    if (selectedProductCategory.id === -1) return [];
    const all = productsData?.body?.products || [];
    const seen = new Set();
    const options = [];
    for (let i = 0; i < all.length; i++) {
      const p = all[i];
      if (
        p.category_id == selectedProductCategory.id &&
        p.name &&
        !seen.has(p.name)
      ) {
        seen.add(p.name);
        options.push({ id: options.length + 1, name: p.name });
      }
    }
    return options;
  }, [productsData, selectedProductCategory]);

  const gwSizeOptions = useMemo(() => {
    if (gwProduct.id === -1) return [];
    const all = productsData?.body?.products || [];
    const seen = new Set();
    const sizes = [];
    for (let i = 0; i < all.length; i++) {
      const p = all[i];
      const size = String(p.size ?? "");
      if (
        p.category_id == selectedProductCategory.id &&
        p.name === gwProduct.name &&
        size !== "" &&
        !seen.has(size)
      ) {
        seen.add(size);
        sizes.push(size);
      }
    }
    sizes.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return sizes.map((name, i) => ({ id: i + 1, name }));
  }, [productsData, selectedProductCategory, gwProduct]);

  const gwColorOptions = useMemo(() => {
    if (gwProduct.id === -1 || selectedSize.id === -1) return [];
    const all = productsData?.body?.products || [];
    const seen = new Set();
    const colors = [];
    for (let i = 0; i < all.length; i++) {
      const p = all[i];
      const color = String(p.color ?? "");
      if (
        p.category_id == selectedProductCategory.id &&
        p.name === gwProduct.name &&
        String(p.size) === selectedSize.name &&
        color !== "" &&
        !seen.has(color)
      ) {
        seen.add(color);
        colors.push(color);
      }
    }
    colors.sort((a, b) => a.localeCompare(b));
    return colors.map((name, i) => ({ id: i + 1, name }));
  }, [productsData, selectedProductCategory, gwProduct, selectedSize]);

  // Resolved Variant
  const activeProduct = useMemo(() => {
    if (
      gwProduct.id === -1 ||
      selectedSize.id === -1 ||
      selectedColor.id === -1
    ) {
      return NONE;
    }
    const all = productsData?.body?.products || [];
    const match = all.find(
      (p) =>
        p.category_id == selectedProductCategory.id &&
        p.name === gwProduct.name &&
        String(p.size) === selectedSize.name &&
        String(p.color) === selectedColor.name
    );
    return match ? { ...match, id: match.product_id } : NONE;
  }, [productsData, selectedProductCategory, gwProduct, selectedSize, selectedColor]);

  useEffect(() => {
    if (activeProduct.id !== -1) {
      setMrpPerPiece(Number(activeProduct?.mrp || 0));
      setPiecePerBox(1);
    }
  }, [activeProduct]);

  const handleGenerate = (e) => {
    e.preventDefault();
    toast.dismiss();

    if (gwProduct.id === -1) {
      toast.error("select product", { id: "select-product" });
      return;
    }
    if (selectedSize.id === -1) {
      toast.error("select size", { id: "select-size" });
      return;
    }
    if (selectedColor.id === -1) {
      toast.error("select colour", { id: "select-colour" });
      return;
    }
    if (activeProduct.id === -1) {
      toast.error("select product", { id: "select-product" });
      return;
    }
    if (selectedBatch.id === -1) {
      toast.error("select batch", { id: "select-batch" });
      return;
    }
    if (!qty || Number(qty) <= 0) {
      toast.error("Quantity should be more than 0", { id: "qty-error" });
      return;
    }
    if (qrType == "2" && activeProduct.qty === 0) {
      toast.error("Product Qty is Invalid", { id: "invalid-product-qty" });
      return;
    }
    if (qrType == "2" && qty > 0 && qty % activeProduct.qty !== 0) {
      toast.error(
        "Invalid Qr count, count must be in multiples of product quantity",
        { id: "invalid-qty" }
      );
      return;
    }
    if (!piecePerBox || Number(piecePerBox) <= 0) {
      toast.error("Piece per box should be more than 0", {
        id: "piece-per-box-error",
      });
      return;
    }

    const calculatedBoxPrice =
      (Number(mrpPerPiece) || 0) * (Number(piecePerBox) || 0);
    const finalBoxPrice = Number.isInteger(calculatedBoxPrice)
      ? calculatedBoxPrice
      : parseFloat(calculatedBoxPrice.toFixed(2));

    let data = {
      batch_id: selectedBatch.batch_id,
      batch_code: selectedBatch.name,
      product_code: activeProduct.product_code,
      product_name: activeProduct.name,
      qty: qty,
      // For Goodwill, mrp is passed as (MRP PER PIECE * PIECE PER BOX), i.e., the total Box Price
      mrp: finalBoxPrice,
      details: {
        mrp_per_piece: Number(mrpPerPiece),
        piece_per_box: Number(piecePerBox),
        box_price: finalBoxPrice,
      },
    };

    addQrCode({
      token,
      tenant_id: tenantId,
      body: data,
    });
  };

  useEffect(() => {
    const id = "qrGenerate";
    if (QrCodeIsLoading) {
      toast.loading("Generating QR", { id: id });
    }

    if (QrCodeData) {
      toast.success("Generated Successfully", {
        id: id,
      });
      setTimeout(() => {
        navigate(`/genefied/${getVendorName()}/printQr`);
      }, 200);
    }

    if (QrCodeError) {
      toast.error(QrCodeError?.data?.message || "Some Error Occurred", {
        id: id,
      });
    }
  }, [QrCodeData, QrCodeError, QrCodeIsLoading]);

  // Original field width styling
  const fieldCls = (goodwillWidth) => `mb-6 bottom-2 ${goodwillWidth}`;

  const categoryLocked = false;
  const productLocked = selectedProductCategory.id === -1;
  const sizeLocked = gwProduct.id === -1;
  const colourLocked = selectedSize.id === -1;

  return (
    <>
      {productsIsLoading && <CircleLoaderComponent />}
      {subCategoriesIsLoading && <CircleLoaderComponent />}

      <div className="flex w-full bg-gray-200 h-full justify-center">
        <div className="w-full ">
          <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
            Qr Code Generation
            <LinksComponent />
          </div>
          <div className="p-3">
            <div className=" p-3 w-full bg-white shadow rounded-lg">
              <form className="">
                <div className="w-full flex justify-center space-x-4">
                  {/* Category */}
                  <div className={fieldCls("w-1/6")}>
                    <LockedField locked={categoryLocked}>
                      <SelectComponentWithSearch
                        title={"Category"}
                        selected={selectedProductCategory}
                        setSelected={setSelectedProductCategory}
                        dataArr={
                          subCategoriesData ? subCategoriesData?.body : []
                        }
                        required={true}
                        placeholder={"Select Category"}
                      />
                    </LockedField>
                  </div>

                  {/* Product */}
                  <div className={fieldCls("w-1/4")}>
                    <LockedField locked={productLocked}>
                      <SelectComponentWithSearch
                        title={"Product"}
                        selected={gwProduct}
                        setSelected={setGwProduct}
                        dataArr={gwProductOptions}
                        required={true}
                        placeholder={
                          productLocked
                            ? "Select Category First"
                            : "Select Product"
                        }
                      />
                    </LockedField>
                  </div>

                  {/* Size */}
                  <div className={fieldCls("w-1/6")}>
                    <LockedField locked={sizeLocked}>
                      <SelectComponentWithSearch
                        title={"Size"}
                        selected={selectedSize}
                        setSelected={setSelectedSize}
                        dataArr={gwSizeOptions}
                        required={true}
                        placeholder={
                          sizeLocked ? "Select Product First" : "Select Size"
                        }
                      />
                    </LockedField>
                  </div>

                  {/* Colour */}
                  <div className={fieldCls("w-1/6")}>
                    <LockedField locked={colourLocked}>
                      <SelectComponentWithSearch
                        title={"Colour"}
                        selected={selectedColor}
                        setSelected={setSelectedColor}
                        dataArr={gwColorOptions}
                        required={true}
                        placeholder={
                          colourLocked
                            ? "Select Size First"
                            : "Select Colour"
                        }
                      />
                    </LockedField>
                  </div>

                  {/* Batch */}
                  <div className={fieldCls("w-1/6")}>
                    <SelectComponentWithSearch
                      title={"Batch"}
                      selected={selectedBatch}
                      setSelected={setSelectedBatch}
                      dataArr={batchList}
                      required={true}
                      placeholder={"Select Batch"}
                    />
                  </div>

                  {/* Quantity */}
                  <div className={fieldCls("w-1/6")}>
                    <InputComponent
                      name={"Quantity"}
                      type={"number"}
                      value={qty}
                      setValue={setQty}
                      required={true}
                      placeholder={"Enter Quantity"}
                    />
                  </div>
                </div>

                {activeProduct?.id != -1 && (
                  <div className="flex flex-wrap items-end justify-start gap-3 mt-4">
                    {/* MRP PER PIECE */}
                    <div className="w-40">
                      <InputComponent
                        name={"MRP PER PIECE"}
                        type={"number"}
                        placeholder={"Enter MRP Per Piece"}
                        value={mrpPerPiece}
                        setValue={(val) => {
                          if (val === "" || Number(val) >= 0) {
                            setMrpPerPiece(val);
                          }
                        }}
                      />
                    </div>

                    {/* PIECE PER BOX */}
                    <div className="w-40">
                      <InputComponent
                        name={"PIECE PER BOX"}
                        type={"number"}
                        placeholder={"Enter Piece Per Box"}
                        value={piecePerBox}
                        setValue={(val) => {
                          if (val === "" || Number(val) >= 0) {
                            setPiecePerBox(val);
                          }
                        }}
                      />
                    </div>

                    {/* Box Price */}
                    <div className="w-40">
                      <InputComponent
                        name={"Box Price"}
                        type={"number"}
                        value={boxPrice}
                        disabled={true}
                      />
                    </div>
                  </div>
                )}

                {!QrCodeIsLoading && (
                  <div className="flex justify-end mt-2">
                    <button
                      type="button"
                      onClick={(e) => handleGenerate(e)}
                      className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center "
                    >
                      Generate
                    </button>
                  </div>
                )}
              </form>
            </div>

            <div>
              {activeProduct.id === -1 ? (
                <div className="w-full text-center"> No Product Selected</div>
              ) : (
                <div className="max-w-4xl my-2 mx-auto flex bg-white  rounded overflow-hidden shadow-lg">
                  <div className="p-0">
                    {activeProduct.images && (
                      <img
                        src={
                          activeProduct.images.length > 0 &&
                          activeProduct.images[0]
                        }
                        alt={activeProduct?.name}
                        className="w-full h-24 object-cover"
                      />
                    )}
                    <div className="px-4 py-2">
                      <div className="font-bold text-xl mb-2">
                        {activeProduct?.name}
                      </div>
                      <p className="text-gray-700 text-base">
                        {activeProduct?.description}
                      </p>
                    </div>
                  </div>
                  <div>
                    <div className="px-4 py-2">
                      <span className="inline-block bg-gray-200 rounded-full px-2 py-1 text-sm font-semibold text-gray-700 mr-2">
                        {activeProduct?.category_name}
                      </span>
                      <span className="inline-block bg-gray-200 rounded-full px-2 py-1 text-sm font-semibold text-gray-700">
                        {activeProduct?.brand_name}
                      </span>
                    </div>
                    <div className="px-4 py-2">
                      <span className="inline-block bg-gray-200 rounded-full px-2 py-1 text-sm font-semibold text-gray-700 mr-2">
                        Size : {activeProduct?.size}
                      </span>
                      <span className="inline-block bg-gray-200 rounded-full px-2 py-1 text-sm font-semibold text-gray-700">
                        Colour : {activeProduct?.color}
                      </span>
                    </div>
                    <div className="px-4 py-2">
                      <span className="inline-block capitalize bg-gray-200 rounded-full px-2 py-1 text-sm font-semibold text-gray-700 mr-2">
                        {activeProduct?.unit_name} (
                        {activeProduct?.uom_value} {activeProduct?.uom_key})
                      </span>
                      <span className="inline-block bg-gray-200 rounded-full px-2 py-1 text-sm font-semibold text-gray-700">
                        Qty : {activeProduct?.qty}
                      </span>
                    </div>
                  </div>

                  <div>
                    <div className="px-4 py-2">
                      <p className="text-gray-700 text-sm">
                        Product Code: <b>{activeProduct?.product_code}</b>
                      </p>
                      <p className="text-gray-700 text-sm">
                        Status:{" "}
                        <b>
                          {activeProduct?.status === "1"
                            ? "Active"
                            : "Inactive"}
                        </b>
                      </p>
                      <p className="text-gray-700 text-sm">
                        Points Active:{" "}
                        <b>
                          {activeProduct?.points_active === "1"
                            ? "Yes"
                            : "No"}
                        </b>
                      </p>
                      <p className="text-gray-700 text-sm">
                        Warranty Activation Period:{" "}
                        <b>
                          {activeProduct?.warranty_activation_period} days
                        </b>
                      </p>
                    </div>
                    <div className="px-4 py-2">
                      <p className="text-gray-700 text-sm">
                        Created by: <b>{activeProduct?.created_by_name}</b> on{" "}
                        <b>
                          {new Date(
                            activeProduct?.created_at
                          ).toLocaleDateString()}
                        </b>
                      </p>
                      <p className="text-gray-700 text-sm">
                        Last updated by:{" "}
                        <b>{activeProduct?.updated_by_name}</b> on{" "}
                        <b>
                          {new Date(
                            activeProduct?.updated_at
                          ).toLocaleDateString()}
                        </b>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QrGeneration;
